import { FC, useEffect, useRef, useState, JSX, useMemo } from 'react';
import {
  Box,
  Stack,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Tooltip,
  Grid,
} from '@mui/material';
import Chip from 'ui-component/extended/Chip';
import {
  IconBox,
  IconBuilding,
  IconCalendarEvent,
  IconCopy,
  IconExternalLink,
  IconFileSpreadsheet,
  IconLink,
  IconListDetails,
  IconPackage,
  IconPlaneDeparture,
  IconReceipt2,
  IconShoppingCart,
  IconShoppingCartQuestion,
  IconTruckDelivery,
  IconMailQuestion,
  IconReportMoney,
  IconCpu,
  IconPlus,
  IconMinus,
  IconCirclePlus,
  Icon,
  IconTag,
  IconUser,
  IconFileDollar,
  IconSend,
} from '@tabler/icons-react';
import { RelatedRecord, RelatedRecordType } from 'types/inventory';
import { useNavigate } from 'react-router-dom';
import { ALL_APPS, ALL_APP_IDS, ALL_VIEWS } from 'constants/appConstants';
import { appPermissionStates, RecordType } from 'types/apps';
import _ from 'lodash';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import { useSelector, useDispatch } from 'store';
import { setStoredOrg } from 'store/actions';
import { setActiveOrgId } from 'store/slices/apiV1/org';
import { orgFromUserOrgId } from 'utils/userPermissions';
import { useGetUserQuery } from 'store/slices/apiV1/user';
import SegmentIconButton from 'ui-component/Segment/SegmentIconButton';
import { copyClipboard } from 'utils/functions';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import useCreateLinkToRecord from 'hooks/useCreateLinkToRecord';
import {
  ALL_RECORD_TYPES,
  relatedRecordTypeToRecordType,
} from 'constants/recordTypes';
import { PizzaTrackerStep } from 'types/pizzaGraph';
import PizzaTracker from 'ui-component/PizzaGraph/PizzaTracker';
import {
  GenericResource,
  ListResponse,
  QueryFilterOperators,
  QueryParams,
} from 'types/api';
import { BaseQueryFn } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import {
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/dist/query/fetchBaseQuery';
import { UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { QueryDefinition } from '@reduxjs/toolkit/dist/query/react';
import useServerSideQuery from 'hooks/useServerSideQuery';
import { useTheme } from '@mui/material/styles';

interface BaseRelatedRecordChipProps {
  id: string;
}

interface PizzaTrackerRelatedRecordChipProps
  extends BaseRelatedRecordChipProps {
  recordType: RecordType<GenericResource>;
}

interface RelatedRecordChipProps extends BaseRelatedRecordChipProps {
  label: string;
  link?: string;
  icon: JSX.Element;
  orgId: string | null;
  recordTypeLabel: string;
  tooltip?: JSX.Element;
  noLink?: boolean;
}

export const relatedRecordTypeToIcon: Record<RelatedRecordType, Icon> = {
  [RelatedRecordType.STOCK_LOT]: IconPackage,
  [RelatedRecordType.STOCK_LOCATION]: IconBuilding,
  [RelatedRecordType.SHIPMENT]: IconPlaneDeparture,
  [RelatedRecordType.ORDER]: IconTruckDelivery,
  [RelatedRecordType.PURCHASE_ORDER]: IconFileSpreadsheet,
  [RelatedRecordType.SNAPSHOT]: IconShoppingCart,
  [RelatedRecordType.PRODUCTION_RUN]: IconCalendarEvent,
  [RelatedRecordType.BOM]: IconListDetails,
  [RelatedRecordType.KIT_REQUEST]: IconBox,
  [RelatedRecordType.SUPPLIER_BILL]: IconReceipt2,
  [RelatedRecordType.PURCHASE_REQUEST_GROUP]: IconShoppingCartQuestion,
  [RelatedRecordType.RFQ]: IconMailQuestion,
  [RelatedRecordType.SUPPLIER_QUOTE]: IconReportMoney,
  [RelatedRecordType.PART]: IconCpu,
  [RelatedRecordType.USER]: IconUser,
  [RelatedRecordType.TEAM]: IconTag,
  [RelatedRecordType.CART_PURCHASE_REQUEST_GROUP]: IconShoppingCart,
  [RelatedRecordType.RFQ_PURCHASE_REQUEST_GROUP]: IconSend,
  [RelatedRecordType.SUPPLIER_PURCHASE_ORDER]: IconFileDollar,
};

export const RelatedRecordChip: FC<RelatedRecordChipProps> = ({
  id,
  label,
  link,
  icon,
  orgId,
  recordTypeLabel,
  tooltip,
  noLink = false,
}) => {
  const theme = useTheme();
  const { activeOrgId } = useSelector((appState) => appState.org);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: user } = useGetUserQuery();

  const { createLinkWithActiveOrgId } = useCreateLinkToRecord();

  const chipElRef = useRef<HTMLDivElement | null>(null);
  const labelElRef = useRef<HTMLSpanElement | null>(null);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [isChipOverflowed, setIsChipOverflowed] = useState<boolean>(false);

  useEffect(() => {
    const handleResize = () => {
      if (labelElRef.current) {
        setIsChipOverflowed(
          labelElRef.current.scrollWidth > labelElRef.current.clientWidth
        );
      }
    };

    if (chipElRef.current && labelElRef.current) {
      window.addEventListener('resize', handleResize);
      handleResize();
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [chipElRef, labelElRef, setIsChipOverflowed]);

  return (
    <Box key={id}>
      {!noLink && link && (
        <Menu
          id="menu"
          anchorEl={chipElRef.current}
          keepMounted
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          variant="selectedMenu"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem
            onClick={(event) => {
              if (orgId && orgId !== activeOrgId) {
                dispatch(setStoredOrg(orgId));
                dispatch(
                  setActiveOrgId({
                    orgId,
                    org: orgFromUserOrgId(user, orgId),
                    skipResetState: true,
                    blended: false,
                  })
                );
              }
              event.stopPropagation();
              event.preventDefault();
              navigate(link);
            }}
          >
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText>Go To {recordTypeLabel}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (orgId && orgId !== activeOrgId) {
                dispatch(setStoredOrg(orgId));
                dispatch(
                  setActiveOrgId({
                    orgId,
                    org: orgFromUserOrgId(user, orgId),
                    skipResetState: true,
                    blended: false,
                  })
                );
              }
              window.open(`${window.location.origin}${link}`, '_blank');
              setMenuOpen(false);
            }}
          >
            <ListItemIcon>
              <IconExternalLink />
            </ListItemIcon>
            <ListItemText>Open {recordTypeLabel} In New Tab</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              copyClipboard(
                createLinkWithActiveOrgId(`${window.location.origin}${link}`)
              );
              setMenuOpen(false);
            }}
          >
            <ListItemIcon>
              <IconCopy />
            </ListItemIcon>
            <ListItemText>Copy Link to {recordTypeLabel}</ListItemText>
          </MenuItem>
        </Menu>
      )}
      <Tooltip
        slotProps={
          tooltip
            ? {
                tooltip: {
                  sx: {
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: theme.palette.grey[300],
                    borderRadius: '100px',
                    height: '50px',
                    p: 1,
                    maxWidth: 'none',
                    width: 'auto',
                    minWidth: 'fit-content',
                  },
                },
              }
            : undefined
        }
        placement="top"
        title={tooltip || (isChipOverflowed ? label : '')}
      >
        <Box>
          <Chip
            slotProps={{
              root: { ref: chipElRef },
              label: { ref: labelElRef },
            }}
            sx={{ '& svg': { minWidth: 20 } }}
            label={label}
            variant="outlined"
            icon={icon}
            clickable={!noLink && !!link}
            color="primary"
            type="button"
            onMouseDown={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
            onClick={
              noLink || !link
                ? undefined
                : (event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setMenuOpen(true);
                  }
            }
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

const RelatedRecordChipPizzaTracker: FC<PizzaTrackerRelatedRecordChipProps> = ({
  id,
  recordType,
}) => {
  const useGetQuery = recordType?.useGetQuery as UseQuery<
    QueryDefinition<
      QueryParams<GenericResource>,
      BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
      string,
      ListResponse<GenericResource>
    >
  >;

  const { data, isFetching } = useServerSideQuery<
    GenericResource,
    GenericResource
  >(useGetQuery, {
    pageSize: 1,
    schema: recordType.statusSchema,
    filters: [
      {
        field: 'id',
        operator: QueryFilterOperators['='],
        value: id,
      },
    ],
  });

  const pizzaTrackerSteps: PizzaTrackerStep[] | undefined = useMemo(
    () =>
      _.first(data?.data) && recordType?.statusGetter
        ? recordType.statusGetter?.(_.first(data?.data) as GenericResource)
        : undefined,
    [data?.data, recordType?.statusGetter]
  );

  if (isFetching) {
    return (
      <Skeleton
        variant="rounded"
        width={280}
        height={32}
        sx={{ borderRadius: '100px' }}
      />
    );
  }

  if (!pizzaTrackerSteps) {
    return null;
  }

  return <PizzaTracker steps={pizzaTrackerSteps} />;
};

interface RelatedRecordsProps {
  relatedRecords: RelatedRecord[] | null | undefined;
  onOpenEditRelatedRecords?: () => void;
  isLoading?: boolean;
  EditButton?: JSX.Element | undefined;
  initialDisplayLimit?: number;
  onAddRelatedRecordClick?: () => void;
}

const recordTypeToApp: {
  [key in RelatedRecordType]: keyof typeof ALL_APP_IDS;
} = {
  [RelatedRecordType.STOCK_LOT]: ALL_APP_IDS.STOCK,
  [RelatedRecordType.STOCK_LOCATION]: ALL_APP_IDS.MANAGED_INVENTORY,
  [RelatedRecordType.SHIPMENT]: ALL_APP_IDS.KITTING,
  [RelatedRecordType.ORDER]: ALL_APP_IDS.ORDERS,
  [RelatedRecordType.SNAPSHOT]: ALL_APP_IDS.ORDERS,
  [RelatedRecordType.PRODUCTION_RUN]: ALL_APP_IDS.PRODUCTION,
  [RelatedRecordType.BOM]: ALL_APP_IDS.BOMS,
  [RelatedRecordType.PURCHASE_ORDER]: ALL_APP_IDS.PROCUREMENT,
  [RelatedRecordType.KIT_REQUEST]: ALL_APP_IDS.KITTING,
  [RelatedRecordType.SUPPLIER_BILL]: ALL_APP_IDS.PROCUREMENT,
  [RelatedRecordType.PURCHASE_REQUEST_GROUP]: ALL_APP_IDS.REQUISITIONS,
  [RelatedRecordType.RFQ]: ALL_APP_IDS.SOURCING,
  [RelatedRecordType.SUPPLIER_QUOTE]: ALL_APP_IDS.SOURCING,
  [RelatedRecordType.USER]: ALL_APP_IDS.USERS,
  [RelatedRecordType.TEAM]: ALL_APP_IDS.PROPERTIES,
  [RelatedRecordType.PART]: ALL_APP_IDS.PARTS,
  [RelatedRecordType.CART_PURCHASE_REQUEST_GROUP]:
    ALL_APP_IDS.SIMPLIFIED_BUYING_ORDERS,
  [RelatedRecordType.RFQ_PURCHASE_REQUEST_GROUP]: ALL_APP_IDS.SOURCING,
  [RelatedRecordType.SUPPLIER_PURCHASE_ORDER]:
    ALL_APP_IDS.SIMPLIFIED_BUYING_ORDERS,
};

type RecordTypeConfigItem =
  | {
      icon: JSX.Element;
      link: string;
      recordTypeLabel: string;
      noLink?: false;
    }
  | {
      icon: JSX.Element;
      link?: never;
      recordTypeLabel: string;
      noLink: true;
    };

type RecordTypeConfig = {
  [key in RelatedRecordType]: RecordTypeConfigItem;
};

const RelatedRecords: FC<RelatedRecordsProps> = ({
  relatedRecords,
  onOpenEditRelatedRecords,
  isLoading,
  EditButton,
  initialDisplayLimit = 3,
  onAddRelatedRecordClick,
}) => {
  const { appPermissions } = useAppAccessContext();
  const { blended } = useSelector((appState) => appState.org);
  const [isExpanded, setIsExpanded] = useState(false);

  const filteredRelatedRecords = relatedRecords?.filter(
    (r) =>
      _.get(
        appPermissions,
        [recordTypeToApp[r.recordType], 'state'],
        appPermissionStates.disabled
      ) === appPermissionStates.enabled ||
      (_.get(
        appPermissions,
        [recordTypeToApp[r.recordType], 'state'],
        appPermissionStates.disabled
      ) === appPermissionStates.noBlended &&
        blended)
  );

  const hasMoreRecords =
    filteredRelatedRecords &&
    filteredRelatedRecords.length > initialDisplayLimit;
  const displayedRecords = isExpanded
    ? filteredRelatedRecords
    : filteredRelatedRecords?.slice(0, initialDisplayLimit);

  return (
    <Box>
      <Stack spacing={1} justifyContent="flex-start">
        {isLoading && filteredRelatedRecords?.length === 0 && (
          <Skeleton variant="rectangular" width={120} height={36} />
        )}
        <Grid container spacing={1}>
          {displayedRecords &&
            displayedRecords.map((related: RelatedRecord) => {
              const RelatedRecordIcon = related?.Icon;

              const recordTypeName =
                relatedRecordTypeToRecordType[related.recordType];
              const recordTypeDefinition = recordTypeName
                ? ALL_RECORD_TYPES[recordTypeName]
                : null;
              const usePizzaTracker =
                recordTypeDefinition?.statusGetter &&
                recordTypeDefinition?.statusSchema &&
                recordTypeDefinition?.useGetQuery;

              const recordTypeConfig: Partial<RecordTypeConfig> = {
                [RelatedRecordType.STOCK_LOT]: {
                  icon: RelatedRecordIcon ? (
                    <RelatedRecordIcon />
                  ) : (
                    <IconPackage />
                  ),
                  link: `${ALL_APPS.STOCK.views?.STOCK_LOTS?.path}/${related.recordId}`,
                  recordTypeLabel: 'Stock Lot',
                },
                [RelatedRecordType.STOCK_LOCATION]: {
                  icon: RelatedRecordIcon ? (
                    <RelatedRecordIcon />
                  ) : (
                    <IconBuilding />
                  ),
                  recordTypeLabel: 'Stock Location',
                  noLink: true,
                },
                [RelatedRecordType.SHIPMENT]: {
                  icon: RelatedRecordIcon ? (
                    <RelatedRecordIcon />
                  ) : (
                    <IconPlaneDeparture />
                  ),
                  link: `${ALL_APPS.KITTING.views?.SHIPMENTS?.path}/${related.recordId}`,
                  recordTypeLabel: 'Shipment',
                },
                [RelatedRecordType.ORDER]: {
                  icon: RelatedRecordIcon ? (
                    <RelatedRecordIcon />
                  ) : (
                    <IconTruckDelivery />
                  ),
                  link: `${ALL_APPS.ORDERS.views?.ORDERS_TABLE?.path}/${related.recordId}`,
                  recordTypeLabel: 'Order',
                },
                [RelatedRecordType.PURCHASE_ORDER]: {
                  icon: RelatedRecordIcon ? (
                    <RelatedRecordIcon />
                  ) : (
                    <IconFileSpreadsheet />
                  ),
                  link: `${ALL_VIEWS.PURCHASE_ORDERS.path}/${related.recordId}`,
                  recordTypeLabel: 'Purchase Order',
                },
                [RelatedRecordType.SNAPSHOT]: {
                  icon: RelatedRecordIcon ? (
                    <RelatedRecordIcon />
                  ) : (
                    <IconShoppingCart />
                  ),
                  link: `${ALL_APPS.ORDERS.views?.YOUR_ORDERS?.path}/${related.recordId}`,
                  recordTypeLabel: 'Cart',
                },
                [RelatedRecordType.PRODUCTION_RUN]: {
                  icon: RelatedRecordIcon ? (
                    <RelatedRecordIcon />
                  ) : (
                    <IconCalendarEvent />
                  ),
                  link: `${ALL_APPS.PRODUCTION.path}/${related.recordId}`,
                  recordTypeLabel: 'Program',
                },
                [RelatedRecordType.BOM]: {
                  icon: RelatedRecordIcon ? (
                    <RelatedRecordIcon />
                  ) : (
                    <IconListDetails />
                  ),
                  link: `${ALL_APPS.BOMS.path}/${related.recordId}`,
                  recordTypeLabel: 'BOM',
                },
                [RelatedRecordType.KIT_REQUEST]: {
                  icon: RelatedRecordIcon ? <RelatedRecordIcon /> : <IconBox />,
                  link: `${ALL_APPS.KITTING.views?.KITS?.path}/${related.recordId}`,
                  recordTypeLabel: 'Kit',
                },
                [RelatedRecordType.SUPPLIER_BILL]: {
                  icon: RelatedRecordIcon ? (
                    <RelatedRecordIcon />
                  ) : (
                    <IconReceipt2 />
                  ),
                  link: `${ALL_VIEWS.SUPPLIER_BILLS.path}/${related.recordId}`,
                  recordTypeLabel: 'Supplier Bill',
                },
                [RelatedRecordType.PURCHASE_REQUEST_GROUP]: {
                  icon: RelatedRecordIcon ? (
                    <RelatedRecordIcon />
                  ) : (
                    <IconShoppingCartQuestion />
                  ),
                  link: `${ALL_VIEWS.REQUISITIONS_TABLE.path}/${related.recordId}`,
                  recordTypeLabel: 'Requisition',
                },
                [RelatedRecordType.RFQ]: {
                  icon: RelatedRecordIcon ? (
                    <RelatedRecordIcon />
                  ) : (
                    <IconMailQuestion />
                  ),
                  link: `${ALL_VIEWS.RFQ.path}/${related.recordId}`,
                  recordTypeLabel: 'RFQ',
                },
                [RelatedRecordType.SUPPLIER_QUOTE]: {
                  icon: RelatedRecordIcon ? (
                    <RelatedRecordIcon />
                  ) : (
                    <IconReportMoney />
                  ),
                  link: `${ALL_VIEWS.QUOTE.path}/${related.recordId}`,
                  recordTypeLabel: 'Quote',
                },
                [RelatedRecordType.PART]: {
                  icon: RelatedRecordIcon ? <RelatedRecordIcon /> : <IconCpu />,
                  link: `${ALL_APPS.PARTS.path}/${related.recordId}`,
                  recordTypeLabel: 'Part',
                },
                [RelatedRecordType.CART_PURCHASE_REQUEST_GROUP]: {
                  icon: RelatedRecordIcon ? (
                    <RelatedRecordIcon />
                  ) : (
                    <IconShoppingCart />
                  ),
                  link: `${ALL_VIEWS.ALL_ORDERS.path}/${related.recordId}`,
                  recordTypeLabel: 'Order',
                },
                [RelatedRecordType.RFQ_PURCHASE_REQUEST_GROUP]: {
                  icon: RelatedRecordIcon ? (
                    <RelatedRecordIcon />
                  ) : (
                    <IconSend />
                  ),
                  link: `${ALL_VIEWS.SOURCING_RFQS.path}/${related.recordId}`,
                  recordTypeLabel: 'RFQ',
                },
                [RelatedRecordType.SUPPLIER_PURCHASE_ORDER]: {
                  icon: RelatedRecordIcon ? (
                    <RelatedRecordIcon />
                  ) : (
                    <IconFileDollar />
                  ),
                  link: `${ALL_VIEWS.SUPPLIER_ORDERS.path}/${related.recordId}`,
                  recordTypeLabel: 'Supplier Order',
                },
              };

              const config = recordTypeConfig[
                related.recordType as keyof typeof recordTypeConfig
              ] || {
                icon: <IconLink />,
                link: '#',
                recordTypeLabel: related.recordType,
                noLink: false,
              };

              return (
                <Grid key={related.recordId}>
                  {usePizzaTracker ? (
                    <RelatedRecordChip
                      id={related.recordId}
                      label={related.name}
                      link={config.noLink ? undefined : config.link}
                      icon={config.icon}
                      orgId={related.orgId}
                      recordTypeLabel={config.recordTypeLabel}
                      tooltip={
                        <RelatedRecordChipPizzaTracker
                          id={related.recordId}
                          recordType={
                            recordTypeDefinition as RecordType<GenericResource>
                          }
                        />
                      }
                      noLink={config.noLink}
                    />
                  ) : (
                    <RelatedRecordChip
                      id={related.recordId}
                      label={related.name}
                      link={config.noLink ? undefined : config.link}
                      icon={config.icon}
                      orgId={related.orgId}
                      recordTypeLabel={config.recordTypeLabel}
                      noLink={config.noLink}
                    />
                  )}
                </Grid>
              );
            })}
          {onOpenEditRelatedRecords && (
            <Grid>
              <SegmentIconButton
                aria-label="Edit Related Program"
                title="Edit Related Program"
                size="small"
                onClick={onOpenEditRelatedRecords}
              >
                <IconLink size="20px" />
              </SegmentIconButton>
            </Grid>
          )}
          {onAddRelatedRecordClick && (
            <Grid>
              <SegmentIconButton
                color="primary"
                onClick={onAddRelatedRecordClick}
                size="small"
              >
                <IconCirclePlus width={20} height={20} />
              </SegmentIconButton>
            </Grid>
          )}
          {EditButton && <Grid>{EditButton}</Grid>}
        </Grid>
        {hasMoreRecords && (
          <SegmentButton
            size="small"
            aria-label={
              isExpanded
                ? 'Show Less'
                : `Show ${
                    filteredRelatedRecords.length - initialDisplayLimit
                  } More`
            }
            onClick={() => setIsExpanded(!isExpanded)}
            startIcon={
              isExpanded ? <IconMinus size={16} /> : <IconPlus size={16} />
            }
          >
            {isExpanded
              ? 'Show Less'
              : `Show ${
                  filteredRelatedRecords.length - initialDisplayLimit
                } More`}
          </SegmentButton>
        )}
      </Stack>
    </Box>
  );
};

export default RelatedRecords;
